import React, { useEffect, useState } from "react";
import { Card, Table, Badge, Button, Dropdown } from "react-bootstrap";
import { toast } from 'react-toastify';
import Pagination from 'react-js-pagination';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMapMarkerAlt, faUser, faPhone, faEnvelope, faCalendarAlt, faHome } from '@fortawesome/free-solid-svg-icons';
import Api from "../../Api";

const Appointments = () => {
  const api = Api();
  const [appointmentsList, setAppointmentsList] = useState<any[]>([]);
  const [appointmentsCount, setAppointmentsCount] = useState(0);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const onChangePageIndex = (pageNumber: number) => setPageIndex(pageNumber - 1);
  const onChangePageSize = (e: any) => setPageSize(parseInt(e.target.value));

  const fetchAppointments = () => {
    api.appointmentRequest.search({
      pageIndex,
      pageSize
    }).then((response: any) => {
      const data = response.result.data;
      setAppointmentsList(data.appointmentRequestsList || []);
      setAppointmentsCount(data.appointmentRequestsListCount || 0);
    });
  };

  const handleStatusChange = (requestId: number, statusId: number) => {
    api.appointmentRequest.updateStatus(requestId, statusId)
      .then((res: any) => {
        toast.success(res.result?.message || 'Status updated successfully');
        fetchAppointments();
      })
      .catch((err: any) => {
        toast.error(err.response?.data?.message || err.response?.data?.error?.message || 'Failed to update status');
      });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchAppointments();
  }, [pageIndex, pageSize]);

  return (
    <div className="appointments-container py-4 px-4">
      <style>{`
        .appointments-container {
          font-family: 'Inter', system-ui, -apple-system, sans-serif;
        }
        .modern-card {
          border: none;
          border-radius: 12px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
          background: #fff;
          overflow: hidden;
        }
        .modern-table {
          margin-bottom: 0;
        }
        .modern-table thead th {
          background-color: #f8f9fa;
          color: #495057;
          font-weight: 700;
          font-size: 0.9rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          border-bottom: 2px solid #e9ecef;
          padding: 1rem;
        }
        .modern-table tbody td {
          padding: 1rem;
          vertical-align: middle;
          border-bottom: 1px solid #f1f3f5;
        }
        .property-img {
          width: 60px;
          height: 60px;
          border-radius: 8px;
          object-fit: cover;
          margin-right: 15px;
        }
        /* Mobile Card Inspiration Styles */
        .mobile-inspiration-card {
          background: #fff;
          border-radius: 20px;
          padding: 20px;
          margin-bottom: 20px;
          box-shadow: 0 8px 24px rgba(0,0,0,0.06);
        }
        .mobile-card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 15px;
        }
        .mobile-card-icon {
          width: 48px;
          height: 48px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.4rem;
        }
        .mobile-card-badge {
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 700;
        }
        .mobile-card-title {
          font-size: 1.15rem;
          font-weight: 700;
          color: #1a202c;
          margin-bottom: 4px;
        }
        .mobile-card-subtitle {
          font-size: 0.9rem;
          color: #4a5568;
          font-weight: 600;
          margin-bottom: 8px;
        }
        .mobile-card-desc {
          font-size: 0.85rem;
          color: #718096;
          line-height: 1.4;
          margin-bottom: 15px;
        }
        .mobile-card-pills {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }
        .mobile-pill {
          background: #f7fafc;
          border: 1px solid #edf2f7;
          border-radius: 20px;
          padding: 6px 14px;
          font-size: 0.75rem;
          color: #4a5568;
          font-weight: 600;
        }
      `}</style>

      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
        <div>
          <h4 className="fw-bold mb-1" style={{ color: '#2d3748' }}>Leads & Appointments</h4>
          <p className="text-muted mb-0 small">Track all property visits and customer leads.</p>
        </div>
      </div>

      <div className="d-none d-md-block">
        <Card className="modern-card mb-4">
          <div className="table-responsive">
            <Table className="modern-table" hover>
              <thead>
                <tr>
                  <th style={{ width: '80px' }}>ID</th>
                  <th>Property</th>
                  <th>Customer Details</th>
                  <th>Dates Requested</th>
                  <th>Status</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {appointmentsList.map((req) => (
                  <tr key={req.requestId}>
                    <td className="fw-bold text-primary" style={{ fontSize: '1.05rem' }}>#{req.requestId}</td>
                    <td>
                      <div className="d-flex align-items-center">
                        <div className="bg-light d-flex align-items-center justify-content-center text-primary rounded property-img">
                          <FontAwesomeIcon icon={faHome} size="2x" />
                        </div>
                        <div className="text-start">
                          <div className="fw-bold text-dark" style={{ fontSize: '1.05rem' }}>
                            {req.property?.propertyTitle || 'Unknown Property'}
                          </div>
                          <div className="text-muted small">
                            <FontAwesomeIcon icon={faMapMarkerAlt} className="me-1 text-danger" /> 
                            {req.property?.propertyLocation?.city ? `${req.property.propertyLocation.city}, ${req.property.propertyLocation.state}` : 'Location N/A'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="d-flex flex-column text-start">
                        <div className="text-dark fw-bold mb-1" style={{ fontSize: '1rem' }}>
                          <FontAwesomeIcon icon={faUser} className="me-2 text-info" style={{ width: '16px' }} />
                          {req.finderDetails?.fullName || 'N/A'}
                        </div>
                        <div className="text-muted small mb-1">
                          <FontAwesomeIcon icon={faEnvelope} className="me-2" style={{ width: '16px' }} />
                          {req.finderDetails?.email || 'N/A'}
                        </div>
                        <div className="text-muted small">
                          <FontAwesomeIcon icon={faPhone} className="me-2 text-success" style={{ width: '16px' }} />
                          {req.finderDetails?.mobile || 'N/A'}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="d-flex flex-column text-start">
                        {req.requestDates && req.requestDates.length > 0 ? req.requestDates.map((d: any, i: number) => (
                          <div key={i} className="text-dark fw-semibold small mb-1">
                            <FontAwesomeIcon icon={faCalendarAlt} className="me-2 text-warning" />
                            {new Date(d.proposedDate).toLocaleString()}
                          </div>
                        )) : 'No Dates'}
                        <div className="text-muted small mt-1">
                          Applied: {new Date(req.createdAt).toLocaleDateString()}
                        </div>
                      </div>
                    </td>
                    <td>
                      <Badge 
                        bg={req.statusId === 12 ? 'success' : req.statusId === 11 ? 'warning' : req.statusId === 10 ? 'danger' : 'primary'} 
                        className="rounded-pill px-3 py-2 fw-bold text-white shadow-sm"
                      >
                        {req.statusId === 8 ? 'Pending Review' : req.statusId === 11 ? 'Date Scheduled' : req.statusId === 12 ? 'Visit Completed' : req.statusId === 10 ? 'Rejected' : 'Unknown Status'}
                      </Badge>
                      <div className="text-muted mt-2" style={{ fontSize: '0.75rem', lineHeight: '1.2' }}>
                        {req.statusId === 8 ? 'Waiting for owner/admin to respond.' : req.statusId === 11 ? 'Customer has requested a visit date.' : req.statusId === 12 ? 'Customer has visited the property.' : req.statusId === 10 ? 'Appointment rejected.' : 'Status code: ' + req.statusId}
                      </div>
                    </td>
                    <td className="text-center">
                      <Dropdown>
                        <Dropdown.Toggle variant="outline-primary" size="sm" className="shadow-sm rounded-pill fw-semibold">
                          Action
                        </Dropdown.Toggle>
                        <Dropdown.Menu className="shadow border-0" style={{ borderRadius: '12px' }}>
                          <Dropdown.Item onClick={() => handleStatusChange(req.requestId, 8)} className="py-2">Mark as Pending</Dropdown.Item>
                          <Dropdown.Item onClick={() => handleStatusChange(req.requestId, 11)} className="py-2">Mark as Scheduled</Dropdown.Item>
                          <Dropdown.Item onClick={() => handleStatusChange(req.requestId, 12)} className="py-2 text-success fw-semibold">Mark as Completed</Dropdown.Item>
                          <Dropdown.Divider />
                          <Dropdown.Item onClick={() => handleStatusChange(req.requestId, 10)} className="py-2 text-danger fw-semibold">Reject</Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </td>
                  </tr>
                ))}
                {appointmentsList.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center py-5 text-muted">
                      No leads or appointments found.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Card>
      </div>

      {/* Mobile Custom Card View */}
      <div className="d-block d-md-none pb-4">
        {appointmentsList.map((req) => (
          <div key={`mob-${req.requestId}`} className="mobile-inspiration-card">
            <div className="mobile-card-header">
              <div className="mobile-card-icon bg-light text-primary">
                <FontAwesomeIcon icon={faHome} />
              </div>
              <Badge 
                bg={req.statusId === 12 ? 'success' : req.statusId === 11 ? 'warning' : req.statusId === 10 ? 'danger' : 'primary'} 
                className="mobile-card-badge shadow-sm"
              >
                {req.statusId === 8 ? 'Pending Review' : req.statusId === 11 ? 'Date Scheduled' : req.statusId === 12 ? 'Visit Completed' : req.statusId === 10 ? 'Rejected' : 'Unknown Status'}
              </Badge>
              <Dropdown>
                <Dropdown.Toggle variant="light" size="sm" className="border-0 shadow-none bg-transparent p-0 text-muted">
                  <i className="fas fa-ellipsis-v fa-lg"></i>
                </Dropdown.Toggle>
                <Dropdown.Menu align="end" className="shadow border-0" style={{ borderRadius: '12px' }}>
                  <Dropdown.Item onClick={() => handleStatusChange(req.requestId, 8)}>Mark as Pending</Dropdown.Item>
                  <Dropdown.Item onClick={() => handleStatusChange(req.requestId, 11)}>Mark as Scheduled</Dropdown.Item>
                  <Dropdown.Item onClick={() => handleStatusChange(req.requestId, 12)} className="text-success fw-semibold">Mark as Completed</Dropdown.Item>
                  <Dropdown.Divider />
                  <Dropdown.Item onClick={() => handleStatusChange(req.requestId, 10)} className="text-danger fw-semibold">Reject</Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>
            
            <div className="mobile-card-title">{req.property?.propertyTitle || 'Unknown Property'}</div>
            <div className="mobile-card-subtitle">@{req.finderDetails?.fullName || 'N/A'}</div>
            <div className="mobile-card-desc">
              Customer interested in visit. <br/>
              Contact: {req.finderDetails?.mobile || 'N/A'}
            </div>
            
            <div className="mobile-card-pills">
              {req.requestDates && req.requestDates.length > 0 ? req.requestDates.map((d: any, i: number) => (
                <span key={i} className="mobile-pill">
                  {new Date(d.proposedDate).toLocaleDateString()}
                </span>
              )) : <span className="mobile-pill">No Dates</span>}
            </div>
          </div>
        ))}
        {appointmentsList.length === 0 && (
          <div className="text-center py-5 text-muted">
            No leads or appointments found.
          </div>
        )}
      </div>
      
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-center bg-white p-3 rounded-3 shadow-sm mb-4">
        <div>
          <span className="me-2 fw-semibold text-muted">Items per page:</span>
          <select 
            value={pageSize} 
            onChange={onChangePageSize} 
            className="form-select form-select-sm d-inline-block w-auto shadow-none border-1"
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </select>
        </div>
        <div className="mt-3 mt-md-0">
          <Pagination
            activePage={pageIndex + 1}
            totalItemsCount={appointmentsCount}
            itemsCountPerPage={pageSize}
            onChange={onChangePageIndex}
            itemClass="page-item"
            linkClass='page-link shadow-none'
            firstPageText="First"
            lastPageText="Last"
          />
        </div>
      </div>
    </div>
  );
};

export default Appointments;
